import { useParams } from 'react-router-dom'
import { toastEventDispatch } from '@hotmart/space-lib-events/dist/toast'
import { ToastType, ToastPosition } from '@hotmart/space-lib-events/dist/toast/enum'
import i18n from 'src/i18n'
import useSendDataHubConsume from 'src/hook/useSendDatahubConsume/useSendDatahubConsume'
import { Actions, Entities, EventNames, ScreenNames } from 'src/utils/datahub/enums'
import { ApiError } from 'src/config/request'
import { ResultsTypes } from 'src/utils/datahub/enums'
import { queryClient } from 'src/config/request'
import { useMemo } from 'react'
import { filterPermission } from 'src/utils/user'
import { PermissionActions } from 'src/services/posts'
import { PERMISSIONS_ACTIONS } from 'src/utils/user/constant'
import { useCommunityDetails } from '../../../context/communityDetailsContext'
import { usePostPinPost } from '../usePostPinPost'
import { usePostUnpinPost } from '../usePostUnpinPost'
import { PathParams, PinPostType } from './types'
import { UsePinPostProps, PinEventsProps } from './types'

const usePinPost = ({ postId }: UsePinPostProps) => {
  const { idCommunity } = useParams() as PathParams
  const { community } = useCommunityDetails()
  const { sendDataHubConsume } = useSendDataHubConsume()

  const { mutate: mutateUnpinPost, isLoading: isLoadingUnpinPost } = usePostUnpinPost()
  const { mutate: mutatePinPost, isLoading: isLoadingPinPost } = usePostPinPost()

  const sendPinPostEvent = ({ type, result, failureKind }: PinEventsProps) => {
    const envetName =
      type === PinPostType.PIN ? EventNames.TRY_TO_PIN_A_POST : EventNames.TRY_TO_UNPIN_A_POST

    sendDataHubConsume({
      action: Actions.VIEW,
      entity: Entities.COMMUNITY,
      event: {
        screenName: ScreenNames.COMMUNITY_FEED,
        eventName: envetName,
        result,
        failureKind
      }
    })
  }

  const invalidateGetPostsQuery = () => {
    queryClient.invalidateQueries({
      queryKey: ['getPosts']
    })

    queryClient.invalidateQueries({
      queryKey: ['getPinnedPosts']
    })
  }

  const hasUserPermissionPinPost = useMemo(() => {
    if (community?.permissions?.actions) {
      return Boolean(
        filterPermission({
          specificPermission: PERMISSIONS_ACTIONS.PIN_CREATE as PermissionActions,
          permissionsUserList: community.permissions.actions as PermissionActions[]
        })
      )
    }

    return false
  }, [community?.permissions])

  const communityId = community?.id || Number(idCommunity)

  const handlePinPost = async () => {
    /* istanbul ignore next */
    mutatePinPost(
      { communityId, postId },
      {
        onSuccess() {
          invalidateGetPostsQuery()

          sendPinPostEvent({
            type: PinPostType.PIN,
            result: ResultsTypes.SUCCESS
          })

          toastEventDispatch({
            type: ToastType.SUCCESS,
            message: i18n.t('pin_post.notifications.pin_success'),
            position: ToastPosition.BOTTOM_RIGHT
          })
        },

        onError(error) {
          sendPinPostEvent({
            type: PinPostType.PIN,
            result: ResultsTypes.FAILURE,
            failureKind: (error as ApiError).message
          })

          toastEventDispatch({
            type: ToastType.DANGER,
            message: i18n.t('pin_post.notifications.pin_error'),
            position: ToastPosition.BOTTOM_RIGHT
          })
        }
      }
    )
  }

  const handleUnpinPost = () => {
    /* istanbul ignore next */
    mutateUnpinPost(
      { communityId, postId },
      {
        onSuccess() {
          invalidateGetPostsQuery()

          sendPinPostEvent({
            type: PinPostType.UNPIN,
            result: ResultsTypes.SUCCESS
          })

          toastEventDispatch({
            type: ToastType.SUCCESS,
            message: i18n.t('pin_post.notifications.unpin_success'),
            position: ToastPosition.BOTTOM_RIGHT
          })
        },

        onError(error) {
          sendPinPostEvent({
            type: PinPostType.UNPIN,
            result: ResultsTypes.FAILURE,
            failureKind: (error as ApiError).message
          })

          toastEventDispatch({
            type: ToastType.DANGER,
            message: i18n.t('pin_post.notifications.unpin_error'),
            position: ToastPosition.BOTTOM_RIGHT
          })
        }
      }
    )
  }

  return {
    hasUserPermissionPinPost,
    handlePinPost,
    handleUnpinPost,
    isLoadingPinPost,
    isLoadingUnpinPost
  }
}

export default usePinPost
